/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { type LoaderFunctionArgs } from 'react-router-dom';

import { customJson } from '@fai/third-party/react-router-dom';

/**
 * @url https://api-dev.friendli.ai/elements/operations/signup_via_invite_callback
 * user redirected via upper callback, might have a query string allocated via BE
 * if following keys are included. FE should render error page
 */
const errorCodeKeys = new Set(['error_code', 'error_description']);

export const strictCheckIfErrorCodesExistsInQueryString = async ({
  request,
}: {
  request: LoaderFunctionArgs['request'];
}) => {
  const error = parseErrorFromQueryString(request.url);

  if (error) {
    // eslint-disable-next-line no-console -- TODO: sync to error page
    console.error(error);

    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw customJson({ message: 'invalid invitation, please contact us' }, { status: 400 });
  }

  return null;
};

const checkIfErrorCodeExistsInQueryString = (url: string): boolean => {
  const queryString = url.split('?')[1];

  if (!queryString) {
    return false;
  }

  const queryParamKeys = queryString.split('&').map((qs) => qs.split('=')[0]);

  return queryParamKeys.some((qs) => errorCodeKeys.has(qs));
};

const parseErrorFromQueryString = (url: string): Record<string, string> | null => {
  if (checkIfErrorCodeExistsInQueryString(url)) {
    const queryString = url.split('?')[1];
    const queryParams: Record<string, string> = {};

    const pairs = queryString.split('&');

    for (const pair of pairs) {
      const [key, value] = pair.split('=');
      queryParams[key] = value;
    }

    return queryParams;
  }

  return null;
};
